import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { taskService } from "@miplatform/api";
import { DAYS_ORDER, ENTITY_NAMES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";

const GET_CAPACITY_UUID = createUUID();

const schema = z.object({
  orderTemplates: z.array(
    z.object({
      orderTemplateId: z.string().uuid(),
      orderTemplateName: z.string(),
      capacities: z.array(
        z.object({
          day: z.string(),
          amount: z
            .string()
            .min(1, "Required")
            .regex(/^[0-9]+$/),
        })
      ),
    })
  ),
});

export function useManageOrderTemplateCapacityViewModel() {
  // ...existing code...
  const stateManager = useComponentState();
  const [orderTemplateCapacities, setOrderTemplateCapacities] = useState([]);

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      orderTemplates: [],
    },
    resolver: zodResolver(schema),
  });

  const handleReset = () => {
    methods.reset({
      orderTemplates: transformDataForForm(orderTemplateCapacities),
    });
  };

  const transformDataForForm = (data) => {
    const orderTemplateIds = [
      ...new Set(data.map((item) => item.orderTemplateId)),
    ];

    return orderTemplateIds.map((orderTemplateId) => {
      const orderTemplateRecords = data.filter(
        (item) => item.orderTemplateId === orderTemplateId
      );
      const firstRecord = orderTemplateRecords[0];
      const sortedCapacities = orderTemplateRecords
        .map((record) => ({
          day: record.day,
          amount: String(record.amount),
        }))
        .sort((a, b) => DAYS_ORDER.indexOf(a.day) - DAYS_ORDER.indexOf(b.day));

      return {
        orderTemplateId: firstRecord.orderTemplateId,
        orderTemplateName: firstRecord.orderTemplateName,
        capacities: sortedCapacities,
      };
    });
  };

  const getOrderTemplateCapacities = async () => {
    stateManager.abort(GET_CAPACITY_UUID);
    stateManager.run(async () => {
      const response = await taskService.orderTemplateCapacities.getAll();

      if (response.status === HTTP_STATUSES.OK) {
        const formattedData = transformDataForForm(response.data);
        setOrderTemplateCapacities(response.data);
        methods.reset({ orderTemplates: formattedData });
      }
    }, GET_CAPACITY_UUID);
  };
  const handleSubmit = async (data) => {
    const flatData = data.orderTemplates.reduce((acc, orderTemplate) => {
      const orderTemplateCapacities = orderTemplate.capacities.map(
        (capacity) => ({
          orderTemplateId: orderTemplate.orderTemplateId,
          orderTemplateName: orderTemplate.orderTemplateName,
          day: capacity.day,
          amount: capacity.amount,
          modifiedOn: new Date().toISOString(),
        })
      );
      return [...acc, ...orderTemplateCapacities];
    }, []);

    stateManager.run(async () => {
      const response = await taskService.orderTemplateCapacities.update({
        data: flatData,
        toastMessageConfig: {
          success: {
            text: `${ENTITY_NAMES.ORDER_TEMPLATE} capacities updated successfully`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderTemplateCapacities(response.data);
        methods.reset({ orderTemplates: transformDataForForm(response.data) });
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    methods,
    handleReset,
    handleSubmit,
    getOrderTemplateCapacities,
  };
}
